import { axiosPost } from '@/api/handlers';
import { useMutation } from '@/api/hooks';
import { useMessage } from '@/providers';
import axios from 'axios';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import {
  AttachmentSchema,
  AttachmentUploadSchema,
} from '../_models/attachment';
import { encodeRfc5987ValueChars } from '../_utils/encoder';

export const useCreateAttachment = () => {
  return useMutation({
    mutationFn: async (values: z.infer<typeof AttachmentSchema>) => {
      const payload = AttachmentSchema.parse(values);
      return await axiosPost('/api/attachments/', payload);
    },
  });
};

export const useUploadFile = () => {
  return useMutation({
    mutationFn: async (values: z.infer<typeof AttachmentUploadSchema>) => {
      const { presignedUrl, file } = AttachmentUploadSchema.parse(values);
      const encodedFileName = encodeRfc5987ValueChars(file.name);
      return await axios.put(presignedUrl, file, {
        headers: {
          'Content-Type': file.type,
          'Content-Disposition': `filename*=UTF-8''${encodedFileName}`,
          'Access-Control-Allow-Methods': 'GET,PUT,POST,OPTIONS',
          'Access-Control-Allow-Origin': location.origin,
        },
      });
    },
  });
};

export const useUploadAttachments = () => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();

  const { mutateAsync: createAttachmentAsync } = useCreateAttachment();
  const { mutateAsync: uploadFileAsync } = useUploadFile();

  const uploadAttachments = useCallback(
    async (files: File[], isThrowWhenErrorCaught?: boolean) => {
      if (files.length === 0) {
        return [];
      }

      try {
        const attachmentIds: string[] = await Promise.all(
          files.map(async (file) => {
            const attachment = await createAttachmentAsync({
              fileName: file.name,
              fileContentType: file.type,
            });

            await uploadFileAsync({
              presignedUrl: attachment.presignedUrl,
              file,
            });

            return attachment.id;
          }),
        );

        return attachmentIds;
      } catch {
        if (isThrowWhenErrorCaught) {
          throw new Error();
        }
        await openMessage('error', t('error-message-with-file'));
      }
    },
    [createAttachmentAsync, uploadFileAsync, t, openMessage],
  );

  return { uploadAttachments };
};
